import { addWeeks, startOfWeek, addDays, format, isWeekend, isBefore, startOfDay } from 'date-fns';

interface LifecycleStage {
  stage: string;
  label: string;
  week_offset: number;
  duration_weeks: number;
  description: string;
  estimated_minutes: number;
  setup: string;
}

interface UserPlantWithDetails {
  id: string;
  plant_id: string;
  planted_date: string | null;
  setup_type: string | null;
  status: string | null;
  plants: {
    name: string;
    plant_type: string;
    lifecycle_stages: LifecycleStage[];
  };
}

interface TimePrefs {
  weekday_minutes: number;
  weekday_count: number;
  weekend_hours_min: number;
  weekend_hours_max: number;
}

export interface GeneratedTask {
  user_plant_id: string;
  task_type: string;
  title: string;
  description: string;
  scheduled_date: string;
  estimated_minutes: number;
}

// Zone-based sow start dates (week of year to start indoor sowing)
const ZONE_SOW_WEEK: Record<string, number> = {
  'UK-South': 6,
  'UK-Midlands': 8,
  'UK-North': 9,
  'UK-Scotland': 10,
  'UK-Wales': 8,
  'UK-NI': 9,
};

// Stages that are universal regardless of setup
const UNIVERSAL_STAGES = new Set([
  'plant_out', 'harvest', 'preserve', 'feed', 'prune', 'support',
  'earth_up', 'thin_out', 'pinch', 'mulch', 'weed', 'harden_off',
  'spring_feed', 'autumn_cut_back', 'harvest_ongoing',
]);

// Stages that should always be included
const ALWAYS_INCLUDE_STAGES = new Set([
  'seed_sowing', 'chit',
]);

// Stages to skip for established perennials (they don't start from seed)
const SEED_STAGES = new Set([
  'seed_sowing', 'chit', 'first_transplant', 'second_transplant', 'harden_off', 'plant_out',
]);

// Stages that only apply when sowing indoors (skip for direct sowing)
const INDOOR_ONLY_STAGES = new Set([
  'harden_off', 'plant_out', 'first_transplant', 'second_transplant',
]);

// Recurring stages get one task per week (not every 3 days)
const RECURRING_STAGES = new Set([
  'harvest', 'feed', 'preserve', 'support', 'weed',
]);

// "Harvest as needed" stages — periodic reminders (every 2 weeks) rather than weekly
const ONGOING_STAGES = new Set([
  'harvest_ongoing',
]);

export function generateSchedule(
  userPlants: UserPlantWithDetails[],
  zone: string,
  timePrefs: TimePrefs,
  availableSetups: string[] = ['direct'],
  seasonYear: number = new Date().getFullYear(),
  existingMinutesPerDay: Record<string, number> = {}
): GeneratedTask[] {
  const today = startOfDay(new Date());
  const sowWeek = ZONE_SOW_WEEK[zone] || 8;
  const seasonStart = startOfWeek(new Date(seasonYear, 0, 1 + (sowWeek - 1) * 7), { weekStartsOn: 1 });
  const allTasks: GeneratedTask[] = [];

  const setupSet = new Set(availableSetups);
  setupSet.add('direct');

  for (const userPlant of userPlants) {
    const stages = (userPlant.plants.lifecycle_stages as unknown as LifecycleStage[]) || [];
    const plantName = userPlant.plants.name;
    const isEstablished = userPlant.status === 'established' || userPlant.status === 'active';

    // Determine if this plant will be sown indoors or direct
    const seedStage = stages.find(s => s.stage === 'seed_sowing');
    const seedSetupIsIndoor = seedStage && !['direct'].includes(seedStage.setup);
    const userCanSowIndoors = [...setupSet].some(s =>
      ['windowsill', 'greenhouse', 'heated_propagator', 'cold_frame'].includes(s)
    );
    const willSowIndoors = !!(seedSetupIsIndoor && userCanSowIndoors);

    const plantAnchorDate = userPlant.planted_date
      ? startOfDay(new Date(userPlant.planted_date))
      : (isBefore(seasonStart, today) ? today : seasonStart);

    for (const stage of stages) {
      // Skip seed/transplant stages for established perennials
      if (isEstablished && SEED_STAGES.has(stage.stage)) {
        continue;
      }

      // Skip transplant-related stages when sowing direct
      if (!willSowIndoors && INDOOR_ONLY_STAGES.has(stage.stage)) {
        continue;
      }

      // Filter by setup availability
      if (!ALWAYS_INCLUDE_STAGES.has(stage.stage) && !UNIVERSAL_STAGES.has(stage.stage)) {
        if (!setupSet.has(stage.setup)) {
          continue;
        }
      }

      // Generate appropriate title — seed_sowing gets indoor/outdoor indicator
      const taskTitle = stage.stage === 'seed_sowing'
        ? (willSowIndoors ? `${plantName}: Sow Seeds Indoors` : `${plantName}: Sow Direct`)
        : `${plantName}: ${stage.label}`;

      const startDate = addWeeks(plantAnchorDate, stage.week_offset);

      if (ONGOING_STAGES.has(stage.stage) && stage.duration_weeks > 1) {
        // "Harvest as needed" — fortnightly reminders for herbs etc.
        const totalWeeks = stage.duration_weeks;
        const endDate = addWeeks(startDate, totalWeeks);
        const endStr = format(endDate, 'd MMM');
        
        for (let week = 0; week < totalWeeks; week += 2) {
          const taskDate = startOfDay(addWeeks(startDate, week));
          if (isBefore(taskDate, today)) continue;

          allTasks.push({
            user_plant_id: userPlant.id,
            task_type: stage.stage,
            title: taskTitle,
            description: `${stage.description} (ongoing until ${endStr})`,
            scheduled_date: format(taskDate, 'yyyy-MM-dd'),
            estimated_minutes: stage.estimated_minutes,
          });
        }
      } else if (RECURRING_STAGES.has(stage.stage) && stage.duration_weeks > 1) {
        // Truly recurring tasks: one per week (harvest, feed, etc.)
        const totalWeeks = stage.duration_weeks;
        const endDate = addWeeks(startDate, totalWeeks);
        const endStr = format(endDate, 'd MMM');
        
        for (let week = 0; week < totalWeeks; week++) {
          const taskDate = startOfDay(addWeeks(startDate, week));
          if (isBefore(taskDate, today)) continue;

          allTasks.push({
            user_plant_id: userPlant.id,
            task_type: stage.stage,
            title: taskTitle,
            description: `${stage.description} (weekly until ${endStr})`,
            scheduled_date: format(taskDate, 'yyyy-MM-dd'),
            estimated_minutes: stage.estimated_minutes,
          });
        }
      } else {
        // Do-once task
        const taskDate = startOfDay(startDate);
        if (isBefore(taskDate, today)) continue;

        allTasks.push({
          user_plant_id: userPlant.id,
          task_type: stage.stage,
          title: taskTitle,
          description: stage.description,
          scheduled_date: format(taskDate, 'yyyy-MM-dd'),
          estimated_minutes: stage.estimated_minutes,
        });
      }
    }
  }

  // Sort by date
  allTasks.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

  // Fit tasks into available time slots, then deduplicate
  const distributed = distributeTasks(allTasks, timePrefs, existingMinutesPerDay);
  return deduplicateTasks(distributed);
}

function distributeTasks(tasks: GeneratedTask[], prefs: TimePrefs, existingMinutes: Record<string, number> = {}): GeneratedTask[] {
  if (tasks.length === 0) return [];

  // Seed with existing time usage from already-scheduled tasks
  const minutesUsed = new Map<string, number>();
  for (const [dateStr, mins] of Object.entries(existingMinutes)) {
    minutesUsed.set(dateStr, mins);
  }
  const result: GeneratedTask[] = [];

  const getUsed = (dateStr: string): number => minutesUsed.get(dateStr) || 0;

  // Build a pool of available day slots for each week, starting from today
  // Weekdays get weekday_minutes each, weekends get a fair share (not all at once)
  const getMaxMinutes = (dateObj: Date): number => {
    if (isWeekend(dateObj)) {
      // Split weekend hours across both days to avoid one big block
      const totalWeekendMins = ((prefs.weekend_hours_min + prefs.weekend_hours_max) / 2) * 60;
      return Math.round(totalWeekendMins / 2);
    }
    return prefs.weekday_minutes;
  };

  // Check if a weekday is an "active" gardening day
  // Spread weekday_count days evenly across Mon-Fri
  const activeWeekdays = getActiveWeekdays(prefs.weekday_count);

  const isDayAvailable = (dateObj: Date): boolean => {
    if (isWeekend(dateObj)) return true;
    const dow = dateObj.getDay(); // 0=Sun, 1=Mon...
    return activeWeekdays.has(dow);
  };

  const findBestDate = (idealDateStr: string, minutes: number): string => {
    const idealDate = new Date(idealDateStr);
    // Look within the same week first (±3 days), then expand
    for (let radius = 0; radius <= 14; radius++) {
      // Try both directions from ideal date
      for (const offset of radius === 0 ? [0] : [-radius, radius]) {
        const candidate = addDays(idealDate, offset);
        const candidateStr = format(candidate, 'yyyy-MM-dd');
        // Don't schedule in the past
        if (isBefore(candidate, startOfDay(new Date()))) continue;
        if (!isDayAvailable(candidate)) continue;
        const max = getMaxMinutes(candidate);
        if (getUsed(candidateStr) + minutes <= max) {
          return candidateStr;
        }
      }
    }
    // Fallback: scan forward
    let current = idealDate;
    for (let i = 0; i < 90; i++) {
      const dateStr = format(current, 'yyyy-MM-dd');
      if (!isBefore(current, startOfDay(new Date()))) {
        const max = getMaxMinutes(current);
        if (getUsed(dateStr) + minutes <= max) {
          return dateStr;
        }
      }
      current = addDays(current, 1);
    }
    return idealDateStr;
  };

  for (const task of tasks) {
    const dateStr = findBestDate(task.scheduled_date, task.estimated_minutes);
    minutesUsed.set(dateStr, getUsed(dateStr) + task.estimated_minutes);
    result.push({ ...task, scheduled_date: dateStr });
  }

  result.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  return result;
}

/** Pick which weekdays (1=Mon..5=Fri) are active based on count */
function getActiveWeekdays(count: number): Set<number> {
  // Spread evenly: 1→Wed, 2→Tue+Thu, 3→Mon+Wed+Fri, 4→Mon+Tue+Thu+Fri, 5→all
  const patterns: Record<number, number[]> = {
    1: [3],           // Wed
    2: [2, 4],        // Tue, Thu
    3: [1, 3, 5],     // Mon, Wed, Fri
    4: [1, 2, 4, 5],  // Mon, Tue, Thu, Fri
    5: [1, 2, 3, 4, 5],
  };
  return new Set(patterns[Math.min(Math.max(count, 1), 5)] || [1, 3, 5]);
}

/** Remove duplicate tasks (same title + same date) that arise from overflow redistribution */
function deduplicateTasks(tasks: GeneratedTask[]): GeneratedTask[] {
  const seen = new Set<string>();
  return tasks.filter(task => {
    const key = `${task.title}|${task.scheduled_date}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
